import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { DeleteOutlined, KeyboardArrowDown } from '@mui/icons-material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import dayjs, { Dayjs } from 'dayjs';
import { useRecoilValue } from 'recoil';
import {
  mdiAccountCircleOutline,
  mdiCheck,
  mdiMapMarker,
  mdiMapOutline,
  mdiTextBoxOutline,
  mdiVideoOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import './EditEventMenu.scss';
import {
  selectedDateAtom,
  useAuthContext,
  useCalendarContext,
  useEventDetailsMenuContext,
  useRecurringEventScopeModal,
} from '../../../../data-access';
import { EventTimeSelector } from '../EventTimeSelector';
import { RBCEvent, transformRBCEventToCalendarEvent } from '../../CalendarTimeline';
import { EventDescription } from '../EventDescription';
import {
  canManageCalendarEventRecurrence,
  getCurrentUserResponseStatus,
  getEventRecurrenceString,
  getInviteResponseChipLabel,
  getInviteResponseTitle,
  getResponseStatusOptions,
  hasSameDate,
  isSupportedResponseStatus,
  SupportedEventResponseStatus,
} from '../helpers';
import { EventCategories } from '../EventCategories';
import { EventAttendees } from '../EventAttendees';
import { CalendarEvent, EnergyFeedbackType, EventCategorySet } from '@demind-inc/core';
import { isEqual } from 'lodash';
import clsx from 'clsx';
import { isAppleEvent } from '../../../../helpers';
import { isAppleCalendarDesktopSupported } from '../../../../helpers/appleCalendarDesktop.helpers';
import { EventEnergyFeedback } from '../EventEnergyFeedback';
import { BasicDialog, EventMetrics } from '../../../common';
import { EventLocationAutocomplete } from '../EventLocationAutocomplete';
import { useTranslation } from 'react-i18next';

interface EditEventMenuProps {
  visible: boolean;
  onClose: () => void;
}

let defaultEvent: RBCEvent | undefined; // Use this for checking if the event has changed

const EditEventMenu: React.FC<EditEventMenuProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState<Dayjs>(selectedDate);
  const [endDate, setEndDate] = useState<Dayjs>(selectedDate.add(30, 'minutes'));
  const [selectedCategory, setSelectedCategory] = useState<EventCategorySet | null>(null);
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<EnergyFeedbackType>();
  const [isAllDay, setIsAllDay] = useState<boolean>(false);
  const [selectedResponseStatus, setSelectedResponseStatus] = useState<
    SupportedEventResponseStatus | ''
  >('');
  const [showDescriptionToolbar, setShowDescriptionToolbar] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [responseMenuAnchorEl, setResponseMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const {
    selectedEvent,
    handleSaveEvent: handleSaveEventApi,
    handleRespondToEvent,
    isRespondingToEvent,
    handleDeleteEvent,
    forceEnableToSave,
  } = useEventDetailsMenuContext();
  const { user } = useAuthContext();
  const { mainCalendar, findCalendarItem, checkCalendarHassAccessToModifyEvent } =
    useCalendarContext();
  const { requestRecurringEventScope } = useRecurringEventScopeModal();
  const isAppleDesktopSupported = isAppleCalendarDesktopSupported();

  const { targetCalendar } = useMemo(() => {
    if (!selectedEvent) return {};
    const targetCalendar = selectedEvent.calendarId
      ? findCalendarItem(selectedEvent.calendarId)
      : mainCalendar;

    return { targetCalendar };
  }, [findCalendarItem, mainCalendar, selectedEvent]);

  const canSelectEnergy = useMemo(
    () => dayjs(selectedEvent?.end).isSameOrBefore(dayjs()),
    [selectedEvent]
  );

  const isAppleCalendarEvent = useMemo(
    () => !!selectedEvent?.calendarId && isAppleEvent(selectedEvent.calendarId, findCalendarItem),
    [selectedEvent?.calendarId, findCalendarItem]
  );
  const isAppleWebBlocked = useMemo(
    () => isAppleCalendarEvent && !isAppleDesktopSupported,
    [isAppleCalendarEvent, isAppleDesktopSupported]
  );
  const isGoogleCalendarEvent = useMemo(
    () => targetCalendar?.calendarType === 'google',
    [targetCalendar?.calendarType]
  );
  const isOutlookCalendarEvent = useMemo(
    () => targetCalendar?.calendarType === 'outlook',
    [targetCalendar?.calendarType]
  );
  const canManageRecurringEvent = useMemo(
    () => canManageCalendarEventRecurrence(targetCalendar?.calendarType, isAppleDesktopSupported),
    [isAppleDesktopSupported, targetCalendar?.calendarType]
  );
  const isRecurringProviderEvent = useMemo(
    () => canManageRecurringEvent && !!getEventRecurrenceString(selectedEvent)?.trim(),
    [canManageRecurringEvent, selectedEvent]
  );
  const currentUserResponseStatus = useMemo(
    () => getCurrentUserResponseStatus(selectedEvent, targetCalendar, user.email),
    [selectedEvent, targetCalendar, user.email]
  );
  const responseStatusOptions = useMemo(() => getResponseStatusOptions(t), [t]);
  const canRespondToEvent = useMemo(
    () =>
      !!selectedEvent?.eventId &&
      !!currentUserResponseStatus &&
      (isGoogleCalendarEvent || isOutlookCalendarEvent),
    [
      currentUserResponseStatus,
      isGoogleCalendarEvent,
      isOutlookCalendarEvent,
      selectedEvent?.eventId,
    ]
  );
  const isResponseMenuOpen = !!responseMenuAnchorEl;
  const isReadOnlyCalendarEvent = useMemo(() => {
    if (!selectedEvent?.calendarId) {
      return false;
    }

    return !checkCalendarHassAccessToModifyEvent(selectedEvent.calendarId);
  }, [checkCalendarHassAccessToModifyEvent, selectedEvent?.calendarId]);

  const areInviteActionsDisabled = useMemo(
    () => isReadOnlyCalendarEvent || isRespondingToEvent,
    [isReadOnlyCalendarEvent, isRespondingToEvent]
  );
  useEffect(() => {
    if (!selectedEvent) {
      return;
    }

    const isSameSelectedEvent =
      defaultEvent?.eventId === selectedEvent.eventId &&
      defaultEvent?.calendarId === selectedEvent.calendarId &&
      hasSameDate(defaultEvent?.start, dayjs(selectedEvent.start));

    // Avoid re-hydrating the entire form when the same event object is patched
    // (e.g. invite response updates) so dirty/save tracking remains stable.
    if (isSameSelectedEvent) {
      return;
    }

    defaultEvent = selectedEvent;
    setTitle(selectedEvent.title || '');
    setStartDate(dayjs(selectedEvent.start));
    setEndDate(dayjs(selectedEvent.end));
    setDescription(selectedEvent.description || '');
    setLocation(selectedEvent.location || '');
    setSelectedFeedback(selectedEvent?.eventMetrics?.energy);
    setSelectedCategory(selectedEvent.categories?.[0] || null);
    setSelectedRecurrence(getEventRecurrenceString(selectedEvent));
    setIsAllDay(selectedEvent.allDay);
    setShowDescriptionToolbar(false);
  }, [selectedEvent]);

  useEffect(() => {
    if (currentUserResponseStatus && isSupportedResponseStatus(currentUserResponseStatus)) {
      setSelectedResponseStatus(currentUserResponseStatus);
      return;
    }

    setSelectedResponseStatus('');
  }, [currentUserResponseStatus]);

  const isStartTimeBeforeEndTime = useMemo(() => startDate.isBefore(endDate), [startDate, endDate]);
  const locationChanged = useMemo(
    () => (defaultEvent?.location || '') !== location.trim(),
    [location]
  );
  const recurrenceChanged = useMemo(
    () => getEventRecurrenceString(defaultEvent) !== selectedRecurrence,
    [selectedRecurrence]
  );
  const shouldPersistRecurrenceChange = canManageRecurringEvent && recurrenceChanged;
  const isRemovingRecurrence = useMemo(
    () => isRecurringProviderEvent && recurrenceChanged && selectedRecurrence === null,
    [isRecurringProviderEvent, recurrenceChanged, selectedRecurrence]
  );
  const isChangingRecurrenceRule = useMemo(
    () => isRecurringProviderEvent && recurrenceChanged && selectedRecurrence !== null,
    [isRecurringProviderEvent, recurrenceChanged, selectedRecurrence]
  );
  const canSave = useMemo(
    () =>
      forceEnableToSave ||
      (!!title &&
        isStartTimeBeforeEndTime &&
        (defaultEvent?.title !== title ||
          defaultEvent?.description !== description ||
          locationChanged ||
          !hasSameDate(defaultEvent?.start, startDate) ||
          !hasSameDate(defaultEvent?.end, endDate) ||
          !isEqual(selectedCategory, defaultEvent?.categories?.[0] ?? null) ||
          defaultEvent?.allDay !== isAllDay ||
          selectedFeedback !== defaultEvent?.eventMetrics?.energy ||
          shouldPersistRecurrenceChange)),
    [
      forceEnableToSave,
      isStartTimeBeforeEndTime,
      title,
      description,
      locationChanged,
      startDate,
      endDate,
      selectedCategory,
      defaultEvent,
      isAllDay,
      selectedFeedback,
      shouldPersistRecurrenceChange,
    ]
  );

  const resetEventForm = () => {
    setTitle('');
    setLocation('');
    setStartDate(selectedDate);
    setEndDate(selectedDate.add(30, 'minutes'));
    setSelectedRecurrence(null);
    setResponseMenuAnchorEl(null);
    defaultEvent = undefined;
  };

  const handleSaveEvent = async () => {
    if (!selectedEvent) {
      return;
    }
    if (isAppleWebBlocked || isReadOnlyCalendarEvent) {
      return;
    }

    const newEventDate: Pick<CalendarEvent, 'start' | 'end'> = {
      start: {
        date: startDate.toISOString(),
        timeZone: dayjs.tz.guess(),
      },
      end: {
        date: isAllDay ? endDate.subtract(1, 'day').toISOString() : endDate.toISOString(),
        timeZone: dayjs.tz.guess(),
      },
    };

    const nextEventPayload = {
      ...transformRBCEventToCalendarEvent(selectedEvent),
      ...newEventDate,
      summary: title,
      description,
      location: location.trim(),
      eventMetrics: { energy: selectedFeedback },
      categories: selectedCategory ? [selectedCategory] : [],
      isAllDay,
      ...(shouldPersistRecurrenceChange
        ? {
            recurrence: selectedRecurrence
              ? ({ string: selectedRecurrence } as any)
              : (null as any),
          }
        : {}),
    };

    if (isRecurringProviderEvent) {
      if (isRemovingRecurrence) {
        handleSaveEventApi(nextEventPayload, {
          recurringUpdateSpan: 'thisEvent',
          originalOccurrenceDate: dayjs(defaultEvent?.start || selectedEvent.start).toISOString(),
        });
      } else if (isChangingRecurrenceRule) {
        handleSaveEventApi(nextEventPayload, {
          recurringUpdateSpan: 'allEvents',
          originalOccurrenceDate: dayjs(defaultEvent?.start || selectedEvent.start).toISOString(),
        });
      } else {
        const scope = await requestRecurringEventScope({
          title: t('calendar.recurringScope.editTitle'),
          message: t('calendar.recurringScope.updateMessage'),
        });

        if (scope === 'cancel') {
          return;
        }

        handleSaveEventApi(nextEventPayload, {
          recurringUpdateSpan: scope,
          originalOccurrenceDate: dayjs(defaultEvent?.start || selectedEvent.start).toISOString(),
        });
      }
    } else {
      handleSaveEventApi(nextEventPayload, {
        ...((isGoogleCalendarEvent ||
          isOutlookCalendarEvent ||
          targetCalendar?.calendarType === 'lifestack') &&
        shouldPersistRecurrenceChange
          ? { recurringUpdateSpan: 'allEvents' as const }
          : {}),
      });
    }

    resetEventForm();
  };

  const handleCancelEvent = () => {
    onClose();
    resetEventForm();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.target as HTMLElement).closest('.event-description') ||
      (event.target as HTMLElement).closest('.event-location-autocomplete')
    ) {
      return;
    }

    if (!canSave) {
      return;
    }

    if (event.key === 'Enter') {
      void handleSaveEvent();
    }
  };

  const handleDeleteAction = async () => {
    if (!selectedEvent) {
      return;
    }
    if (isAppleWebBlocked || isReadOnlyCalendarEvent) {
      return;
    }

    if (isRecurringProviderEvent) {
      const scope = await requestRecurringEventScope({
        title: t('calendar.recurringScope.deleteTitle'),
        message: t('calendar.recurringScope.deleteMessage'),
      });

      if (scope === 'cancel') {
        return;
      }

      handleDeleteEvent(selectedEvent, { recurringDeleteSpan: scope });
      return;
    }

    handleDeleteEvent(selectedEvent);
  };

  const handleDeleteClick = () => {
    if (!selectedEvent || isAppleWebBlocked || isReadOnlyCalendarEvent) {
      return;
    }

    if (isRecurringProviderEvent) {
      void handleDeleteAction();
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    void handleDeleteAction();
    setDeleteConfirmOpen(false);
  };

  const handleEnergyChange = (feedback: EnergyFeedbackType) => {
    setSelectedFeedback(feedback);
  };

  const handleOpenResponseMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (areInviteActionsDisabled) {
      return;
    }

    setResponseMenuAnchorEl(event.currentTarget);
  };

  const handleCloseResponseMenu = () => {
    setResponseMenuAnchorEl(null);
  };

  const handleInviteResponseSelection = async (responseStatus: SupportedEventResponseStatus) => {
    handleCloseResponseMenu();

    if (responseStatus === selectedResponseStatus) {
      return;
    }

    const didUpdateResponse = await handleRespondToEvent(responseStatus);
    if (!didUpdateResponse) {
      return;
    }

    setSelectedResponseStatus(responseStatus);
  };

  const handleLocationPress = () => {
    const trimmedLocation = location.trim();
    if (!trimmedLocation) return;

    if (
      trimmedLocation.includes('https://maps.app.goo.gl') ||
      trimmedLocation.includes('https://www.google.com/maps')
    ) {
      window.open(trimmedLocation, '_blank');
    } else {
      const encodedLocation = encodeURIComponent(trimmedLocation);
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
      window.open(mapsUrl, '_blank');
    }
  };

  if (!selectedEvent) {
    return null;
  }

  return (
    <>
      <Dialog
        open={visible}
        className="edit-event-menu"
        onClose={handleCancelEvent}
        disableRestoreFocus
        onKeyDown={(event) => {
          event.stopPropagation();
          handleKeyDown(event);
        }}
      >
        <DialogContent className="edit-event-menu__content">
          <div className="edit-event-menu__body">
            <TextField
              value={title}
              placeholder={t('calendar.common.addTitle')}
              onChange={(e) => setTitle(e.target.value)}
              variant="standard"
              className="edit-event-menu__title"
              autoFocus
              disabled={isReadOnlyCalendarEvent}
              fullWidth
            />
            <div className="edit-event-menu__fields">
              <EventTimeSelector
                startDateTime={startDate}
                endDateTime={endDate}
                onStartTimeChange={(val) => setStartDate(val)}
                onEndTimeChange={(val) => setEndDate(val)}
                disabled={isReadOnlyCalendarEvent}
                isAllDay={isAllDay}
                setIsAllDay={setIsAllDay}
                showRecurrenceSelector={canManageRecurringEvent && !isReadOnlyCalendarEvent}
                recurrenceValue={selectedRecurrence}
                onRecurrenceChange={setSelectedRecurrence}
              />
              <hr className="edit-event-menu__divider" />
              <EventCategories
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                disabled={isReadOnlyCalendarEvent}
                className="edit-event-menu__row edit-event-menu__row--static"
              />
              <hr className="edit-event-menu__divider" />
              <div className="edit-event-menu__row">
                <Icon path={mdiMapMarker} size="20px" className="edit-event-menu__row__icon" />
                <EventLocationAutocomplete
                  value={location}
                  onChange={setLocation}
                  disabled={isReadOnlyCalendarEvent}
                />
                {location.trim() && (
                  <IconButton
                    aria-label={t('calendar.eventMenu.openLocationInGoogleMaps')}
                    className="edit-event-menu__row__icon-button"
                    onClick={handleLocationPress}
                    size="small"
                    title={t('calendar.eventMenu.openInGoogleMaps')}
                  >
                    <Icon path={mdiMapOutline} size="14px" />
                  </IconButton>
                )}
              </div>
              {!!(selectedEvent?.eventMetrics?.heartRate || selectedEvent?.eventMetrics?.stress) && (
                <>
                  <hr className="edit-event-menu__divider" />
                  <EventMetrics
                    heartRate={selectedEvent.eventMetrics?.heartRate}
                    stress={selectedEvent.eventMetrics?.stress}
                    size="md"
                  />
                </>
              )}
              {selectedEvent?.mtgLink && (
                <>
                  <hr className="edit-event-menu__divider" />
                  <div className="edit-event-menu__row">
                    <Icon
                      path={mdiVideoOutline}
                      size="20px"
                      className="edit-event-menu__row__icon"
                    />
                    <a
                      href={selectedEvent.mtgLink}
                      target="_blank"
                      rel="noreferrer"
                      className="edit-event-menu__row__link"
                    >
                      {selectedEvent.mtgLink}
                    </a>
                  </div>
                </>
              )}
              {!!selectedEvent.attendees?.length && (
                <>
                  <hr className="edit-event-menu__divider" />
                  <EventAttendees
                    attendees={selectedEvent.attendees}
                    className="edit-event-menu__row edit-event-menu__row--multiline"
                  />
                </>
              )}
              {!!targetCalendar && (
                <>
                  <hr className="edit-event-menu__divider" />
                  <div className={clsx('edit-event-menu__row', 'edit-event-menu__row--static')}>
                    <Icon
                      path={mdiAccountCircleOutline}
                      size="20px"
                      className="edit-event-menu__row__icon"
                    />
                    <div
                      className={clsx('edit-event-menu__row__text', 'edit-event-menu__name-text')}
                    >
                      {targetCalendar.name}{' '}
                      {targetCalendar.calendarType === 'outlook' &&
                        ` (${targetCalendar.rootEmail})`}
                    </div>
                  </div>
                </>
              )}
              <hr className="edit-event-menu__divider" />
              <div className="edit-event-menu__description-row">
                <Icon
                  path={mdiTextBoxOutline}
                  size="20px"
                  className="edit-event-menu__description-row__icon"
                />
                <div className="edit-event-menu__description-row__body">
                  <div className="edit-event-menu__description-row__input-row">
                    <EventDescription
                      description={description}
                      onChangeDescription={setDescription}
                      disabled={isReadOnlyCalendarEvent}
                      hideToolbar={!showDescriptionToolbar}
                    />
                    <button
                      type="button"
                      className={clsx('edit-event-menu__description-row__toolbar-btn', {
                        'edit-event-menu__description-row__toolbar-btn--active':
                          showDescriptionToolbar,
                      })}
                      aria-label={t(
                        showDescriptionToolbar
                          ? 'calendar.eventMenu.hideFormattingToolbar'
                          : 'calendar.eventMenu.showFormattingToolbar'
                      )}
                      onClick={() => setShowDescriptionToolbar((prev) => !prev)}
                    >
                      <FormatBoldIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
              {canSelectEnergy && (
                <>
                  <hr className="edit-event-menu__divider" />
                  <EventEnergyFeedback
                    selectedFeedback={selectedFeedback}
                    onSelectFeedback={handleEnergyChange}
                    isDisabled={isReadOnlyCalendarEvent}
                  />
                </>
              )}
            </div>
          </div>
          <div className="edit-event-menu__footer-sticky">
            {canRespondToEvent && (
              <div className="edit-event-menu__response-inline">
                <div className="edit-event-menu__response-inline__label">
                  {t('calendar.invite.going')}
                </div>
                <button
                  type="button"
                  className={clsx('edit-event-menu__response-inline__chip', {
                    'edit-event-menu__response-inline__chip--disabled': areInviteActionsDisabled,
                    'edit-event-menu__response-inline__chip--open': isResponseMenuOpen,
                  })}
                  onClick={handleOpenResponseMenu}
                  disabled={areInviteActionsDisabled}
                  aria-label={t('calendar.invite.goingWithStatus', {
                    status: getInviteResponseTitle(
                      t,
                      selectedResponseStatus || currentUserResponseStatus
                    ),
                  })}
                  aria-haspopup="menu"
                  aria-expanded={isResponseMenuOpen}
                >
                  <span className="edit-event-menu__response-inline__chip__text">
                    {getInviteResponseChipLabel(
                      t,
                      selectedResponseStatus || currentUserResponseStatus
                    )}
                  </span>
                  <KeyboardArrowDown
                    className={clsx('edit-event-menu__response-inline__chip__icon', {
                      'edit-event-menu__response-inline__chip__icon--open': isResponseMenuOpen,
                    })}
                  />
                </button>
              </div>
            )}
            <div className="edit-event-menu__footer">
              <button
                type="button"
                onClick={handleDeleteClick}
                className="edit-event-menu__footer__delete"
                disabled={isAppleWebBlocked || isReadOnlyCalendarEvent}
                aria-label={t('calendar.eventMenu.deleteEvent')}
              >
                <DeleteOutlined />
                <span>{t('calendar.common.delete')}</span>
              </button>
              <div className="edit-event-menu__footer__actions">
                <Button
                  onClick={handleCancelEvent}
                  variant="text"
                  className="edit-event-menu__footer__button edit-event-menu__footer__button--cancel"
                >
                  {t('calendar.common.cancel')}
                </Button>
                <Button
                  disabled={!canSave || isAppleWebBlocked || isReadOnlyCalendarEvent}
                  onClick={() => void handleSaveEvent()}
                  variant="contained"
                  className="edit-event-menu__footer__button edit-event-menu__footer__button--primary"
                >
                  {t('calendar.common.save')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
        <Menu
          anchorEl={responseMenuAnchorEl}
          open={isResponseMenuOpen}
          onClose={handleCloseResponseMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{ className: 'edit-event-menu__response-menu' }}
        >
          {responseStatusOptions.map(({ label, value }) => {
            const isSelected = selectedResponseStatus === value;

            return (
              <MenuItem
                key={value}
                onClick={() => handleInviteResponseSelection(value)}
                className={clsx('edit-event-menu__response-menu__item', {
                  'edit-event-menu__response-menu__item--selected': isSelected,
                })}
              >
                <span className="edit-event-menu__response-menu__item__icon">
                  {isSelected ? <Icon path={mdiCheck} size="12px" /> : null}
                </span>
                <span className="edit-event-menu__response-menu__item__label">{label}</span>
              </MenuItem>
            );
          })}
        </Menu>
      </Dialog>
      <BasicDialog
        visible={deleteConfirmOpen}
        title={t('calendar.eventMenu.deleteEvent')}
        description={t('calendar.eventMenu.deleteEventDescription')}
        ctaString={t('calendar.common.delete')}
        onYes={handleDeleteConfirm}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default EditEventMenu;
